import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Copy, Check, Download, ZoomIn, Eye, ArrowDownToLine, Maximize2, ExternalLink, Wand2, X, FileText, Folder, Globe, Hash, AtSign } from 'lucide-react';
import { signUrl } from '../../services/signedUrlApi';
import useSignedSrc from '../../hooks/useSignedSrc';
import { useTheme } from '../../contexts/ThemeContext';

// QW2 (S56) + S57.5 fix instabilité — Rendu Mermaid lazy-loaded avec
// debounce 400 ms pour absorber le streaming SSE.
//
// Bug avant fix : pendant le stream, `chart` changeait à chaque chunk
// SSE, le useEffect redéclenchait mermaid.render() sur du contenu
// PARTIEL, échec → fallback erreur → re-render → flicker permanent.
//
// Fix : debounce 400 ms — tant que `chart` change toutes les <400 ms,
// le timer reset et aucun render n'est tenté. Quand le stream s'arrête
// (silence > 400 ms), un render unique est lancé sur le chart complet.
//
// React.memo + comparaison custom : ne re-render que si le chart change
// vraiment ou si le thème bascule jour/nuit.
const MermaidDiagram = React.memo(({ chart, isDark }) => {
  const [error, setError] = useState(null);
  const [svg, setSvg] = useState('');

  useEffect(() => {
    let cancelled = false;
    // Debounce : attend la stabilité du chart avant de tenter le render.
    // Pendant le streaming, chaque chunk reset le timer.
    const debounceMs = 400;
    const t = setTimeout(async () => {
      if (cancelled) return;
      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? 'dark' : 'default',
          securityLevel: 'loose',
          fontFamily: 'inherit',
        });
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const { svg: rendered } = await mermaid.render(id, chart);
        if (!cancelled) { setSvg(rendered); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erreur de rendu Mermaid');
      }
    }, debounceMs);
    return () => { cancelled = true; clearTimeout(t); };
  }, [chart, isDark]);

  if (error) {
    return (
      <pre style={{
        background: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 8, padding: 12, margin: '8px 0',
        color: 'var(--text-secondary)', fontSize: 12, whiteSpace: 'pre-wrap',
      }}>
        Mermaid : {error}{'\n\n'}{chart}
      </pre>
    );
  }
  // Fix C (S56) — min-height réservée pendant le lazy-load (await import('mermaid')
  // ~100-300 ms) et pendant le streaming. Évite le jump de hauteur quand le SVG
  // arrive → plus d'oscillation du scroll handler ni du bouton ⬇️.
  const wrapperStyle = {
    background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    border: '1px solid var(--card-border)',
    borderRadius: 8, padding: 16, margin: '12px 0',
    overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: 120, color: 'var(--text-secondary)', fontSize: 12,
  };
  if (svg) {
    return <div className="mermaid-diagram" style={wrapperStyle} dangerouslySetInnerHTML={{ __html: svg }} />;
  }
  return <div className="mermaid-diagram" style={wrapperStyle}>Diagramme en cours de rendu…</div>;
});
MermaidDiagram.displayName = 'MermaidDiagram';

const downloadAsFile = (content, filename) => {
  const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'page.html';
  a.click();
  URL.revokeObjectURL(url);
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        background: copied ? '#22c55e' : 'rgba(255,255,255,0.1)',
        border: 'none', borderRadius: '4px',
        padding: '4px 8px', cursor: 'pointer',
        color: 'white', fontSize: '11px',
        display: 'flex', alignItems: 'center', gap: '4px',
        transition: 'all 0.2s'
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copié' : 'Copier'}
    </button>
  );
};

// ─── Composant image générée avec download + zoom ───────────
// Signed URLs HMAC-SHA256 TTL 10min (plus de JWT 24h en query string).
const GeneratedImage = ({ src, index }) => {
  const [zoomed, setZoomed] = useState(false);
  const isInternalAsset = typeof src === 'string' && src.includes('/api/assets/');
  const signedAssetSrc = useSignedSrc(isInternalAsset ? src : null);
  const displaySrc = isInternalAsset ? (signedAssetSrc || '') : src;

  const handleDownload = async () => {
    try {
      let fetchUrl = src;
      if (isInternalAsset) {
        fetchUrl = await signUrl(`${src}?download=1`);
      }
      const response = await fetch(fetchUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `itriia-image-${Date.now()}-${index || 0}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Erreur téléchargement image:', e);
    }
  };

  return (
    <>
      <div style={{
        position: 'relative',
        display: 'inline-block',
        margin: '8px 0',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid var(--card-border)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
        maxWidth: '100%',
      }}>
        <img
          src={displaySrc}
          alt={`Image générée ${index + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '500px',
            display: 'block',
            cursor: 'pointer',
          }}
          onClick={() => setZoomed(true)}
        />
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          display: 'flex',
          gap: '4px',
        }}>
          <button
            onClick={() => setZoomed(true)}
            style={{
              background: 'rgba(0,0,0,0.6)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              backdropFilter: 'blur(4px)',
            }}
            title="Agrandir"
          >
            <ZoomIn size={14} />
          </button>
          <button
            onClick={handleDownload}
            style={{
              background: 'rgba(29,78,216,0.8)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              backdropFilter: 'blur(4px)',
            }}
            title="Télécharger"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      {/* Modal zoom plein écran */}
      {zoomed && (
        <div
          onClick={() => setZoomed(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'zoom-out',
            backdropFilter: 'blur(8px)',
          }}
        >
          <img
            src={displaySrc}
            alt="Zoom"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 0 40px rgba(29,78,216,0.3)' }}
          />
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
              background: 'rgba(29,78,216,0.9)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(29,78,216,0.4)',
            }}
          >
            <Download size={16} /> Télécharger
          </button>
        </div>
      )}
    </>
  );
};

// ─── Composant document généré avec téléchargement DOCX + PDF ─────────
const GeneratedDocument = ({ filename, url, size, onPreview }) => {
  const handleDownload = async (format) => {
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const pathToSign = format === 'pdf' ? `${url}?format=pdf` : url;
      const signed = await signUrl(pathToSign);
      window.open(`${apiBase}${signed}`, '_blank');
    } catch (e) {
      console.error('Signature URL document échouée:', e);
    }
  };

  return (
    <div
      style={{
        marginTop: '10px', padding: '10px 14px',
        background: 'linear-gradient(135deg, var(--pill-soft-bg) 0%, transparent 100%)',
        border: '1px solid var(--pill-soft-border)', borderRadius: '10px',
        display: 'flex', alignItems: 'center', gap: '11px',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 3px 14px var(--pill-soft-glow)'; e.currentTarget.style.borderColor = 'var(--pill-soft-border-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--pill-soft-border)'; }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px',
        background: 'var(--pill-soft-bg-hover)',
        border: '1px solid var(--pill-soft-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Download size={15} color="var(--pill-soft-icon)" strokeWidth={2.2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
          Document — {size > 0 ? `${Math.round(size / 1024)} Ko` : 'Prêt'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
        <button onClick={() => handleDownload('docx')} style={{
          padding: '3px 9px', borderRadius: '5px', cursor: 'pointer',
          background: 'var(--pill-soft-bg)',
          border: '1px solid var(--pill-soft-border)',
          color: 'var(--pill-soft-text)', fontSize: '10.5px', fontWeight: 700,
          letterSpacing: '0.4px',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--pill-soft-bg-hover)'; e.currentTarget.style.borderColor = 'var(--pill-soft-border-hover)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--pill-soft-bg)'; e.currentTarget.style.borderColor = 'var(--pill-soft-border)'; }}
        >
          .DOCX
        </button>
        <button onClick={() => handleDownload('pdf')} style={{
          padding: '3px 9px', borderRadius: '5px', cursor: 'pointer',
          background: 'rgba(244,114,182,0.06)',
          border: '1px solid rgba(244,114,182,0.22)',
          color: 'var(--pdf-btn-text, #f472b6)', fontSize: '10.5px', fontWeight: 700,
          letterSpacing: '0.4px',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,114,182,0.12)'; e.currentTarget.style.borderColor = 'rgba(244,114,182,0.45)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,114,182,0.06)'; e.currentTarget.style.borderColor = 'rgba(244,114,182,0.22)'; }}
        >
          .PDF
        </button>
        {onPreview && (
          <button onClick={() => onPreview({ type: 'docx', filename, url, size })} style={{
            padding: '3px 9px', borderRadius: '5px',
            cursor: 'pointer', background: 'transparent',
            border: '1px solid var(--card-border)',
            color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 500,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--pill-soft-border-hover)'; e.currentTarget.style.color = 'var(--pill-soft-text)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            Aperçu
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Composant source web (Session 25 — fetch_url) ──────────
const WebSource = ({ url, title, domain }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px', margin: '8px 0',
    borderRadius: 10, border: '1px solid rgba(16, 185, 129, 0.3)',
    background: 'rgba(16, 185, 129, 0.04)',
    transition: 'all 0.2s',
  }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)'; e.currentTarget.style.background = 'rgba(16, 185, 129, 0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)'; e.currentTarget.style.background = 'rgba(16, 185, 129, 0.04)'; }}
  >
    <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(16, 185, 129, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 14 }}>🌐</span>
    </div>
    <div style={{ flex: 1, overflow: 'hidden' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title || domain}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {domain}
      </div>
    </div>
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 28, height: 28, borderRadius: 6, flexShrink: 0,
      background: 'rgba(16, 185, 129, 0.12)', color: '#10b981',
      textDecoration: 'none', transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = '#10b981'; e.currentTarget.style.color = 'white'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)'; e.currentTarget.style.color = '#10b981'; }}
      title="Ouvrir dans un nouvel onglet"
    >
      <ExternalLink size={14} />
    </a>
  </div>
);

// ─── Parser les marqueurs [ITRIIA_DOC:...] et [ITRIIA_WEB:...] ─
const DOC_MARKER_REGEX = /\[ITRIIA_DOC:([^:]+):([^:]+):(\d+)\]/g;
const WEB_MARKER_PIPE = /\[ITRIIA_WEB\|([^|]+)\|([^|]+)\|([^\]]+)\]/g;
const WEB_MARKER_COLON = /\[ITRIIA_WEB:(https?:\/\/[^:]+):([^:]+):([^\]]+)\]/g;

// ─── Regex pour nettoyer les marqueurs [TOOL:...] résiduels ─
const TOOL_MARKER_REGEX = /\[TOOL:\w+:[^\]]*\]/g;
const TOOL_MARKER_TRUNCATED = /\[TOOL:\w+:\{[\s\S]*$/g;

const extractDocuments = (text) => {
  const docs = [];
  const webSources = [];
  let match;
  const docRegex = new RegExp(DOC_MARKER_REGEX.source, 'g');
  while ((match = docRegex.exec(text)) !== null) {
    docs.push({ filename: match[1], url: match[2], size: parseInt(match[3]) });
  }
  // Format nouveau (|) puis ancien (:) pour rétro-compatibilité
  const webPipe = new RegExp(WEB_MARKER_PIPE.source, 'g');
  while ((match = webPipe.exec(text)) !== null) {
    webSources.push({ url: match[1], title: match[2], domain: match[3] });
  }
  const webColon = new RegExp(WEB_MARKER_COLON.source, 'g');
  while ((match = webColon.exec(text)) !== null) {
    if (!webSources.some(w => w.url === match[1])) {
      webSources.push({ url: match[1], title: match[2], domain: match[3] });
    }
  }
  let cleanedText = text
    .replace(DOC_MARKER_REGEX, '')
    .replace(WEB_MARKER_PIPE, '')
    .replace(WEB_MARKER_COLON, '')
    .replace(TOOL_MARKER_REGEX, '')
    .replace(TOOL_MARKER_TRUNCATED, '')
    .trim();
  return { cleanedText, docs, webSources };
};

// ─── Chip Le Dialecte (S55 P1) ────────────────────────────────
// Affiché sous les images générées dans le chat quand Le Dialecte a optimisé le prompt.
// Clic → popover avec original / optimisé / raisonnement + coût.
const DialecteChip = ({ meta }) => {
  const [open, setOpen] = useState(false);
  if (!meta || !meta.optimized) return null;

  const fmtCost = (c) => {
    if (!c || c <= 0) return '—';
    if (c < 0.01) return `$${c.toFixed(5)}`;
    return `$${c.toFixed(4)}`;
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          fontSize: '11px', color: '#a78bfa',
          background: 'transparent', border: 'none', padding: '4px 0',
          cursor: 'pointer', marginTop: '6px',
        }}
        title="Voir comment Le Dialecte a reformulé ton prompt"
      >
        <Wand2 size={12} />
        <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>
          Prompt optimisé par Le Dialecte
        </span>
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)', border: '1px solid rgba(139,92,246,0.35)',
              borderRadius: '14px', padding: '22px',
              width: '92%', maxWidth: '560px', maxHeight: '82vh', overflow: 'auto',
              boxShadow: '0 14px 48px rgba(139,92,246,0.18)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              style={{
                position: 'absolute', top: '10px', right: '10px',
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--text-secondary)', padding: '4px',
              }}
            >
              <X size={18} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <Wand2 size={16} style={{ color: '#a78bfa' }} />
              <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text)' }}>
                Le Dialecte a reformulé ton prompt
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Ta demande
              </div>
              <div style={{
                padding: '10px 12px', background: 'var(--input-bg)',
                border: '1px solid var(--card-border)', borderRadius: '8px',
                fontSize: '13px', color: 'var(--text)', whiteSpace: 'pre-wrap',
              }}>
                {meta.original || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Prompt optimisé
              </div>
              <div style={{
                padding: '10px 12px', background: 'rgba(139,92,246,0.06)',
                border: '1px solid rgba(139,92,246,0.25)', borderRadius: '8px',
                fontSize: '13px', color: 'var(--text)', whiteSpace: 'pre-wrap',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}>
                {meta.optimized}
              </div>
            </div>

            {meta.rationale && (
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  Raisonnement
                </div>
                <div style={{
                  padding: '10px 12px', background: 'var(--input-bg)',
                  borderLeft: '2px solid #8b5cf6', borderRadius: '4px',
                  fontSize: '13px', color: 'var(--text-secondary)',
                  fontStyle: 'italic', lineHeight: '1.5', whiteSpace: 'pre-wrap',
                }}>
                  {meta.rationale}
                </div>
              </div>
            )}

            <div style={{
              fontSize: '11px', color: 'var(--text-secondary)',
              borderTop: '1px solid var(--card-border)', paddingTop: '10px',
            }}>
              Traduit via <code style={{ fontSize: '11px' }}>{meta.model_used || 'inconnu'}</code>
              {meta.target_model && <> · pour <code style={{ fontSize: '11px' }}>{meta.target_model}</code></>}
              {typeof meta.cost_usd === 'number' && <> · {fmtCost(meta.cost_usd)}</>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Premium markers (Session E) ────────────────────────────────
// Marqueurs visuels typés pour les références inline (fichiers, dossiers,
// URLs, identifiants techniques) dans les messages chat. Couleurs sémantiques
// adaptées mode jour / nuit, palette nuit désaturée (pas de bleu vif fatigant
// sur fond sombre).
const FILE_EXT_RE = /\.(docx|doc|pdf|md|markdown|txt|json|js|jsx|ts|tsx|csv|xlsx|xls|png|jpg|jpeg|gif|svg|webp|html|htm|css|sql|sh|bash|yml|yaml|xml|zip|tar|gz)$/i;
const URL_RE = /^https?:\/\//i;
const PATH_LIKE_RE = /^[\w\-_.]+\/[\w\-_./]+$|\/$/;

const detectMarker = (text) => {
  const t = String(text).trim();
  if (URL_RE.test(t)) return { type: 'url', Icon: Globe };
  if (FILE_EXT_RE.test(t)) return { type: 'file', Icon: FileText };
  if (t.endsWith('/') || (PATH_LIKE_RE.test(t) && !t.includes(' '))) return { type: 'folder', Icon: Folder };
  if (/^@\w+/.test(t)) return { type: 'mention', Icon: AtSign };
  return { type: 'code', Icon: Hash };
};

const PremiumMarker = ({ text, asLink = false, href = null }) => {
  const { type, Icon } = detectMarker(text);
  const inner = (
    <>
      <Icon size={11} className="premium-marker-icon" strokeWidth={2.2} />
      <span className="premium-marker-text">{text}</span>
    </>
  );
  if (asLink && href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`premium-marker premium-marker--${type} premium-marker--link`}
      >
        {inner}
      </a>
    );
  }
  return <span className={`premium-marker premium-marker--${type}`}>{inner}</span>;
};

// S49 — disableMath=true : KaTeX est désactivé (utile pour preview de markdown
// contenant des prix "$0,50" qui seraient mal interprétés comme délimiteurs LaTeX).
// S55 P1 — dialecteMeta : si présent, affiche un chip "Prompt optimisé par Le Dialecte"
// sous les images générées (popover modale avec original/optimisé/raisonnement).
const MarkdownMessage = ({ content, images = [], isUser = false, onPreview, disableMath = false, dialecteMeta = null }) => {
  const { isDark } = useTheme();
  if (!content) return null;

  const { cleanedText, docs, webSources } = extractDocuments(content);

  // Anti-doublon image : le round 2 LLM réécrit parfois `![...](url)` dans le texte
  // alors que la MÊME image arrive aussi via la prop `images` (event SSE structuré du
  // tool generate_image). Sans dédoublonnage → image rendue 2× (contenu + prop) tant
  // que le message n'est pas rechargé depuis la DB. On retire de la prop les URLs déjà
  // présentes inline dans le contenu (le contenu garde l'image là où le modèle l'a posée).
  const inlineImageUrls = new Set();
  if (cleanedText) {
    const inlineImgRe = /!\[[^\]]*\]\(\s*([^)\s]+)/g;
    let mImg;
    while ((mImg = inlineImgRe.exec(cleanedText)) !== null) inlineImageUrls.add(mImg[1]);
  }
  const dedupedImages = (images || []).filter((u) => !inlineImageUrls.has(u));

  return (
    <div className="markdown-message">
      <style>{`
        .markdown-message {
          line-height: 1.6;
          font-size: 14px;
          word-break: break-word;
          overflow-x: hidden;
          max-width: 100%;
        }

        .markdown-message p {
          margin: 0 0 8px 0;
        }

        .markdown-message p:last-child {
          margin-bottom: 0;
        }

        .markdown-message h1, .markdown-message h2, .markdown-message h3,
        .markdown-message h4, .markdown-message h5, .markdown-message h6 {
          margin: 16px 0 8px 0;
          font-weight: 600;
          color: var(--text);
        }

        .markdown-message h1 { font-size: 1.3em; }
        .markdown-message h2 { font-size: 1.15em; }
        .markdown-message h3 { font-size: 1.05em; }

        .markdown-message ul, .markdown-message ol {
          margin: 4px 0 8px 0;
          padding-left: 20px;
        }

        .markdown-message li {
          margin: 2px 0;
        }

        .markdown-message li > p {
          margin: 0;
        }

        .markdown-message blockquote {
          border-left: 3px solid #1D4ED8;
          margin: 8px 0;
          padding: 4px 12px;
          color: var(--text-secondary);
          background: rgba(72, 136, 224, 0.04);
          border-radius: 0 6px 6px 0;
        }

        .markdown-message a {
          color: #3b82f6;
          text-decoration: none;
        }

        .markdown-message a:hover {
          text-decoration: underline;
        }

        .markdown-message img {
          max-width: 100%;
          border-radius: 8px;
          margin: 8px 0;
        }

        .markdown-message hr {
          border: none;
          border-top: 1px solid var(--card-border);
          margin: 12px 0;
        }

        .markdown-message .code-block-wrapper {
          position: relative;
          margin: 8px 0;
          border-radius: 8px;
          overflow: hidden;
          max-width: 100%;
          min-width: 0;
          width: 100%;
          box-sizing: border-box;
        }

        .markdown-message .code-block-wrapper pre {
          margin: 0 !important;
          border-radius: 8px !important;
          font-size: 12.5px !important;
          overflow-x: auto !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          /* S55 fix : force le wrap des lignes ininterruptibles (SVG paths, URLs longues)
             pour que la bulle flex respecte son maxWidth sans déborder */
          white-space: pre-wrap !important;
          word-break: break-word !important;
        }
        .markdown-message .code-block-wrapper pre code,
        .markdown-message .code-block-wrapper pre span {
          white-space: pre-wrap !important;
          word-break: break-word !important;
        }

        .markdown-message pre {
          overflow-x: auto;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }

        .markdown-message {
          min-width: 0;
        }

        .markdown-message .code-block-lang {
          position: absolute;
          top: 8px;
          left: 12px;
          font-size: 10px;
          color: rgba(255,255,255,0.4);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          z-index: 1;
        }

        /* ── Premium markers (Session E) ─────────────────────────
           Marqueurs visuels typés pour références inline (fichiers,
           dossiers, URLs, identifiants). Couleurs sémantiques avec
           palette désaturée en mode nuit (pas de bleu vif fatigant).
           Frame holographique : gradient subtle + border + hover glow. */
        .markdown-message .premium-marker {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 1px 7px 1px 6px;
          border-radius: 5px;
          font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
          font-size: 0.86em;
          line-height: 1.45;
          vertical-align: baseline;
          text-decoration: none;
          background: linear-gradient(135deg, var(--mk-bg-from), var(--mk-bg-to));
          border: 1px solid var(--mk-border);
          color: var(--mk-text);
          transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
          white-space: nowrap;
          max-width: 100%;
          overflow: hidden;
        }

        .markdown-message .premium-marker .premium-marker-icon {
          flex-shrink: 0;
          stroke: var(--mk-icon);
          opacity: 0.92;
        }

        .markdown-message .premium-marker .premium-marker-text {
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .markdown-message .premium-marker:hover {
          border-color: var(--mk-border-hover);
          box-shadow: 0 2px 12px var(--mk-glow);
          transform: translateY(-1px);
        }

        .markdown-message .premium-marker--link {
          cursor: pointer;
        }
        .markdown-message .premium-marker--link:hover .premium-marker-icon {
          opacity: 1;
        }

        /* ── Mode JOUR (body.light) ─ palette indigo/teal/ambre douce ── */
        body.light .markdown-message .premium-marker--file {
          --mk-bg-from: rgba(79, 70, 229, 0.06);
          --mk-bg-to: rgba(99, 102, 241, 0.03);
          --mk-border: rgba(79, 70, 229, 0.18);
          --mk-border-hover: rgba(79, 70, 229, 0.42);
          --mk-text: #4338ca;
          --mk-icon: #6366f1;
          --mk-glow: rgba(99, 102, 241, 0.22);
        }
        body.light .markdown-message .premium-marker--folder {
          --mk-bg-from: rgba(13, 148, 136, 0.06);
          --mk-bg-to: rgba(20, 184, 166, 0.03);
          --mk-border: rgba(13, 148, 136, 0.2);
          --mk-border-hover: rgba(13, 148, 136, 0.45);
          --mk-text: #0f766e;
          --mk-icon: #14b8a6;
          --mk-glow: rgba(20, 184, 166, 0.2);
        }
        body.light .markdown-message .premium-marker--url {
          --mk-bg-from: rgba(217, 119, 6, 0.06);
          --mk-bg-to: rgba(245, 158, 11, 0.03);
          --mk-border: rgba(217, 119, 6, 0.22);
          --mk-border-hover: rgba(217, 119, 6, 0.5);
          --mk-text: #b45309;
          --mk-icon: #f59e0b;
          --mk-glow: rgba(245, 158, 11, 0.22);
        }
        body.light .markdown-message .premium-marker--mention {
          --mk-bg-from: rgba(139, 92, 246, 0.06);
          --mk-bg-to: rgba(168, 85, 247, 0.03);
          --mk-border: rgba(139, 92, 246, 0.2);
          --mk-border-hover: rgba(139, 92, 246, 0.45);
          --mk-text: #6d28d9;
          --mk-icon: #8b5cf6;
          --mk-glow: rgba(139, 92, 246, 0.22);
        }
        body.light .markdown-message .premium-marker--code {
          --mk-bg-from: rgba(71, 85, 105, 0.05);
          --mk-bg-to: rgba(100, 116, 139, 0.03);
          --mk-border: rgba(71, 85, 105, 0.18);
          --mk-border-hover: rgba(71, 85, 105, 0.4);
          --mk-text: #475569;
          --mk-icon: #64748b;
          --mk-glow: rgba(100, 116, 139, 0.18);
        }

        /* ── Mode NUIT (body.dark) ─ palette désaturée chaude ──
           Tons ivoire / sable / vert sauge / violet poudré, pas de bleu
           saturé. Texte légèrement chaud (255,250,240 base) → moins
           fatigant que le bleu pur sur noir. */
        body.dark .markdown-message .premium-marker--file {
          --mk-bg-from: rgba(199, 210, 254, 0.06);
          --mk-bg-to: rgba(165, 180, 252, 0.03);
          --mk-border: rgba(199, 210, 254, 0.22);
          --mk-border-hover: rgba(199, 210, 254, 0.5);
          --mk-text: #c7d2fe;
          --mk-icon: #a5b4fc;
          --mk-glow: rgba(165, 180, 252, 0.25);
        }
        body.dark .markdown-message .premium-marker--folder {
          --mk-bg-from: rgba(167, 243, 208, 0.05);
          --mk-bg-to: rgba(110, 231, 183, 0.03);
          --mk-border: rgba(167, 243, 208, 0.22);
          --mk-border-hover: rgba(167, 243, 208, 0.48);
          --mk-text: #a7f3d0;
          --mk-icon: #6ee7b7;
          --mk-glow: rgba(110, 231, 183, 0.22);
        }
        body.dark .markdown-message .premium-marker--url {
          --mk-bg-from: rgba(254, 230, 138, 0.05);
          --mk-bg-to: rgba(252, 211, 77, 0.03);
          --mk-border: rgba(254, 230, 138, 0.24);
          --mk-border-hover: rgba(254, 230, 138, 0.5);
          --mk-text: #fde68a;
          --mk-icon: #fcd34d;
          --mk-glow: rgba(252, 211, 77, 0.24);
        }
        body.dark .markdown-message .premium-marker--mention {
          --mk-bg-from: rgba(221, 214, 254, 0.05);
          --mk-bg-to: rgba(196, 181, 253, 0.03);
          --mk-border: rgba(221, 214, 254, 0.22);
          --mk-border-hover: rgba(221, 214, 254, 0.48);
          --mk-text: #ddd6fe;
          --mk-icon: #c4b5fd;
          --mk-glow: rgba(196, 181, 253, 0.22);
        }
        body.dark .markdown-message .premium-marker--code {
          --mk-bg-from: rgba(226, 232, 240, 0.04);
          --mk-bg-to: rgba(203, 213, 225, 0.025);
          --mk-border: rgba(226, 232, 240, 0.18);
          --mk-border-hover: rgba(226, 232, 240, 0.4);
          --mk-text: #e2e8f0;
          --mk-icon: #cbd5e1;
          --mk-glow: rgba(203, 213, 225, 0.18);
        }

        /* Fallback : si body n'a pas .light / .dark (édition standalone) */
        .markdown-message .premium-marker {
          --mk-bg-from: rgba(79, 70, 229, 0.06);
          --mk-bg-to: rgba(99, 102, 241, 0.03);
          --mk-border: rgba(79, 70, 229, 0.18);
          --mk-border-hover: rgba(79, 70, 229, 0.42);
          --mk-text: #4338ca;
          --mk-icon: #6366f1;
          --mk-glow: rgba(99, 102, 241, 0.22);
        }

        /* Lien markdown classique (ancres internes #...) */
        .markdown-message .markdown-anchor {
          color: var(--text);
          text-decoration: underline;
          text-decoration-color: var(--mk-text, #6366f1);
          text-underline-offset: 2px;
          text-decoration-thickness: 1.5px;
          transition: text-decoration-color 0.18s ease;
        }
        .markdown-message .markdown-anchor:hover {
          text-decoration-color: currentColor;
        }

        .markdown-message table {
          border-collapse: collapse;
          width: 100%;
          margin: 8px 0;
          font-size: 13px;
        }

        .markdown-message th {
          background: var(--input-bg);
          font-weight: 600;
          text-align: left;
          padding: 8px 10px;
          border: 1px solid var(--card-border);
        }

        .markdown-message td {
          padding: 6px 10px;
          border: 1px solid var(--card-border);
        }

        .markdown-message tr:hover td {
          background: rgba(72, 136, 224, 0.02);
        }

        .markdown-message strong {
          font-weight: 600;
          color: var(--text);
        }

        .markdown-message em {
          font-style: italic;
        }

        /* KaTeX — formules scientifiques */
        .markdown-message .katex-display {
          margin: 12px 0;
          padding: 10px 16px;
          background: rgba(29, 78, 216, 0.04);
          border-left: 3px solid rgba(29, 78, 216, 0.3);
          border-radius: 0 8px 8px 0;
          overflow-x: auto;
        }

        .markdown-message .katex {
          font-size: 1.05em;
          color: var(--text);
        }

        .markdown-message .katex-display > .katex {
          font-size: 1.15em;
        }

        .markdown-message .katex-html {
          overflow-x: auto;
          overflow-y: hidden;
          padding: 2px 0;
        }
      `}</style>

      {content && (
        <ReactMarkdown
          remarkPlugins={disableMath ? [remarkGfm] : [remarkGfm, remarkMath]}
          rehypePlugins={disableMath ? [] : [rehypeKatex]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              const codeString = String(children).replace(/\n$/, '');
              const isHtml = match && match[1] === 'html' && codeString.length > 100;

              // QW2 (S56) — Bloc ```mermaid → diagramme rendu (lazy-load)
              if (!inline && match && match[1] === 'mermaid') {
                return <MermaidDiagram chart={codeString} isDark={isDark} />;
              }

              if (!inline && (match || codeString.includes('\n'))) {
                const lang = match ? match[1] : 'text';
                const toolbarButtons = (pos) => (
                  <div style={{
                    position: pos === 'top' ? 'absolute' : 'relative',
                    ...(pos === 'top' ? { top: '6px', right: '6px' } : { marginTop: '-4px', marginBottom: '4px', justifyContent: 'flex-end' }),
                    display: 'flex', gap: '4px', zIndex: 2,
                  }}>
                    {isHtml && onPreview && (
                      <button
                        onClick={() => onPreview({ type: 'html', content: codeString, filename: 'preview.html' })}
                        title="Aperçu HTML"
                        style={{
                          background: 'rgba(249,115,22,0.2)', border: '1px solid rgba(249,115,22,0.4)',
                          borderRadius: '4px', padding: '2px 8px', cursor: 'pointer',
                          color: '#F97316', fontSize: '10px', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <Eye size={10} /> Aperçu
                      </button>
                    )}
                    {isHtml && (
                      <button
                        onClick={() => downloadAsFile(codeString, 'page.html')}
                        title="Télécharger HTML"
                        style={{
                          background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)',
                          borderRadius: '4px', padding: '2px 8px', cursor: 'pointer',
                          color: '#22C55E', fontSize: '10px', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <ArrowDownToLine size={10} /> .html
                      </button>
                    )}
                    {onPreview && codeString.split('\n').length > 5 && (
                      <button
                        onClick={() => onPreview({ type: isHtml ? 'html' : 'code', content: codeString, filename: `code.${lang}`, language: lang })}
                        title="Ouvrir dans le split view"
                        style={{
                          background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
                          borderRadius: '4px', padding: '2px 6px', cursor: 'pointer',
                          color: '#3B82F6', display: 'flex', alignItems: 'center',
                        }}
                      >
                        <Maximize2 size={10} />
                      </button>
                    )}
                    <CopyButton text={codeString} />
                  </div>
                );
                const isLongBlock = codeString.split('\n').length > 15;

                return (
                  <div className="code-block-wrapper">
                    {match && <span className="code-block-lang">{match[1]}</span>}
                    {toolbarButtons('top')}
                    <SyntaxHighlighter
                      style={isDark ? oneDark : oneLight}
                      language={match ? match[1] : 'text'}
                      PreTag="pre"
                      customStyle={{
                        padding: '32px 16px 16px',
                        borderRadius: isLongBlock ? '8px 8px 0 0' : '8px',
                        fontSize: '12.5px'
                      }}
                      {...props}
                    >
                      {codeString}
                    </SyntaxHighlighter>
                    {isLongBlock && (
                      <div style={{
                        background: isDark ? '#1e1e1e' : '#fafafa',
                        borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '0 0 8px 8px', padding: '6px 12px',
                        display: 'flex', justifyContent: 'flex-end', gap: '4px',
                      }}>
                        {isHtml && onPreview && (
                          <button
                            onClick={() => onPreview({ type: 'html', content: codeString, filename: 'preview.html' })}
                            title="Aperçu HTML"
                            style={{
                              background: 'rgba(249,115,22,0.2)', border: '1px solid rgba(249,115,22,0.4)',
                              borderRadius: '4px', padding: '2px 8px', cursor: 'pointer',
                              color: '#F97316', fontSize: '10px', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: '4px',
                            }}
                          >
                            <Eye size={10} /> Aperçu
                          </button>
                        )}
                        {isHtml && (
                          <button
                            onClick={() => downloadAsFile(codeString, 'page.html')}
                            title="Télécharger HTML"
                            style={{
                              background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)',
                              borderRadius: '4px', padding: '2px 8px', cursor: 'pointer',
                              color: '#22C55E', fontSize: '10px', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: '4px',
                            }}
                          >
                            <ArrowDownToLine size={10} /> .html
                          </button>
                        )}
                        {onPreview && (
                          <button
                            onClick={() => onPreview({ type: isHtml ? 'html' : 'code', content: codeString, filename: `code.${lang}`, language: lang })}
                            title="Ouvrir dans le split view"
                            style={{
                              background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
                              borderRadius: '4px', padding: '2px 6px', cursor: 'pointer',
                              color: '#3B82F6', display: 'flex', alignItems: 'center',
                            }}
                          >
                            <Maximize2 size={10} />
                          </button>
                        )}
                        <CopyButton text={codeString} />
                      </div>
                    )}
                  </div>
                );
              }
              // Inline code → premium marker typé (Session E)
              const text = String(children);
              return <PremiumMarker text={text} />;
            },
            a({ href, children }) {
              // URL HTTP → premium marker URL avec icône Globe + ouverture
              // nouvel onglet. Les ancres internes (#consolidate-...) gardent
              // un rendu lien classique.
              if (href && URL_RE.test(href)) {
                const label = Array.isArray(children) && children.length === 1 && typeof children[0] === 'string'
                  ? children[0]
                  : (typeof children === 'string' ? children : null);
                if (label) return <PremiumMarker text={label} asLink href={href} />;
              }
              return <a href={href} target="_blank" rel="noopener noreferrer" className="markdown-anchor">{children}</a>;
            },
            img({ src, alt, ...props }) {
              // Images générées → composant avec zoom + télécharger
              if (src && (src.includes('/generated-images/') || src.includes('/api/assets/') || src.startsWith('data:image'))) {
                return <GeneratedImage src={src} index={0} />;
              }
              return <img src={src} alt={alt} {...props} />;
            }
          }}
        >
          {cleanedText}
        </ReactMarkdown>
      )}

      {/* Documents générés (persistés dans le texte) */}
      {docs.length > 0 && docs.map((doc, i) => (
        <GeneratedDocument key={`doc-${i}`} filename={doc.filename} url={doc.url} size={doc.size} onPreview={onPreview} />
      ))}

      {/* Sources web (Session 25 — fetch_url) */}
      {webSources.length > 0 && webSources.map((ws, i) => (
        <WebSource key={`web-${i}`} url={ws.url} title={ws.title} domain={ws.domain} />
      ))}

      {/* Images générées (dédoublonnées vs les `![...]` déjà rendues inline par le contenu) */}
      {dedupedImages.length > 0 && (
        <div style={{ marginTop: content ? '12px' : '0' }}>
          {dedupedImages.map((imgUrl, i) => (
            <GeneratedImage key={i} src={imgUrl} index={i} />
          ))}
        </div>
      )}
      {/* Chip Le Dialecte (S55 P1) — affiché dès qu'une image est présente (inline OU prop),
          y compris quand l'image a été dédoublonnée et rendue inline dans le contenu. */}
      {dialecteMeta && (inlineImageUrls.size > 0 || dedupedImages.length > 0) && (
        <div style={{ marginTop: '6px' }}>
          <DialecteChip meta={dialecteMeta} />
        </div>
      )}
    </div>
  );
};

export default MarkdownMessage;
